// ConfirmPanel.jsx — an in-app confirmation, in the app's own chrome (ERR-1 /
// COS-6, Alex 2026-07-26).
//
// Replaces window.confirm, which had three faults beyond being ugly:
//   * it is browser chrome, floating away from the row it is asking about;
//   * it is BINARY, and some of these choices have three outcomes (DOER_GUIDE
//     lesson 6). The scope question — "split this one row out, or re-tag the
//     whole item?" — could not be asked at all, so the second intent was moved to
//     a different control and the dialog told you to cancel and go there. That
//     was the right call against window.confirm; it is not a limit we still have.
//   * it renders \n and nothing else, so consequences arrived as a wall of text.
//
// Actions are a LIST, so a question with three answers is asked once, where it
// arises. `tone: 'danger'` marks a destructive/irreversible choice; the first
// action is the primary. Cancel is always present and is never the primary.
import { WARN } from '../icons.js';
import { BTN_PRIMARY, BTN_DESTRUCTIVE, BTN_DESTRUCTIVE_OUTLINE, BTN_SECONDARY } from '../theme-tokens.js';

// actions: [{ label, value, tone?: 'primary' | 'danger' | 'plain' }]
// impact:  optional string[] — consequences, each on its own line. Kept separate
//          from the question so it can be styled as consequence, not prose.
export default function ConfirmPanel({ title, question, impact = [], actions = [], onChoose }) {
  const btn = (a, i) => {
    const primary = i === 0;
    const danger = a.tone === 'danger';
    // Same red as every other destructive control in the app, not this
    // panel's own copy of one (Alex, 2026-08-21: "same red when red" — this
    // used --th-status-deprecated, a different red than --tg-delete-bg).
    const style = danger
      ? (primary ? BTN_DESTRUCTIVE : BTN_DESTRUCTIVE_OUTLINE)
      : (primary ? BTN_PRIMARY : BTN_SECONDARY);
    return (
      <button key={a.value} onClick={() => onChoose(a.value)} style={{ ...style, fontWeight: primary ? 600 : 400 }}>
        {a.label}
      </button>
    );
  };
  return (
    <div
      role="alertdialog"
      aria-label={title || 'Confirm'}
      style={{
        border: '1px solid var(--th-highlight-border)', background: 'var(--th-highlight-bg)',
        borderRadius: 3, padding: '8px 10px', marginBottom: 6, fontSize: 'var(--fs-small)',
      }}
    >
      {title && (
        <div style={{ fontWeight: 700, color: 'var(--th-text-body)', marginBottom: 4 }}>
          <span aria-hidden="true" style={{ color: 'var(--th-status-draft)' }}>{WARN}</span> {title}
        </div>
      )}
      <div style={{ color: 'var(--th-text-body)', whiteSpace: 'pre-wrap', marginBottom: impact.length ? 6 : 8 }}>
        {question}
      </div>
      {impact.length > 0 && (
        <ul style={{ margin: '0 0 8px', paddingLeft: 18, color: 'var(--th-text-secondary)' }}>
          {impact.map((line, i) => <li key={i} style={{ marginBottom: 2 }}>{line}</li>)}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {actions.map(btn)}
        <button onClick={() => onChoose(null)} style={BTN_SECONDARY}>Cancel</button>
      </div>
    </div>
  );
}
